import { useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import FetchFeedback from "../ui/CustomFetchFeedback/CustomFetchFeedback";
import CustomEmptyState from "../ui/CustomEmptyState/CustomEmptyState";
import useBatchHistory from "./hooks/useBatchHistory";
import { useFileUploadFlow } from "./FileUpload/hooks/useFileUploadFlow";
import useBulkUploadsExport from "./hooks/useBulkUploadsExport";
import useBulkUploadsFilters from "./hooks/useBulkUploadsFilters";
import useBulkUploadsStore from "../../stores/bulkUploadsStore";
import useMerchantOptions from "../Transactions/hooks/useMerchantOptions";
import BulkUploadsFilters from "./components/BulkUploadsFilters";
import BulkUploadsTable from "./components/tables/BulkUploadsTable";

function BulkUploadsContent({ isSuperAdmin = false }) {
  const navigate = useNavigate();
  const openBatch = useBulkUploadsStore((state) => state.openBatch);
  const {
    pagination,
    apiFilters,
    managementApiFilters,
    setPagination,
    searchQuery,
    dateRange,
    status,
    merchantId,
    setSearchQuery,
    setDateRange,
    setStatus,
    setMerchantId,
  } = useBulkUploadsFilters(isSuperAdmin);

  const {
    optionsWithAll: merchantOptions,
    isLoading: merchantOptionsLoading,
    isError: merchantOptionsError,
  } = useMerchantOptions(isSuperAdmin);

  const { handleExport, isExporting } = useBulkUploadsExport(
    isSuperAdmin ? managementApiFilters : apiFilters,
  );
  const { content, totalPages, totalElements, isLoading, isFetching, isError, refetch } =
    useBatchHistory(apiFilters, managementApiFilters, isSuperAdmin);

  const { handleResume: resumeBatch } = useFileUploadFlow();

  const onViewDetails = useCallback((row) => {
    openBatch({ id: row.id, batchId: row.batchId });
    navigate("/bulkpayments/batch-summary");
  }, [openBatch, navigate]);

  const onResume = useCallback((row) => resumeBatch(row), [resumeBatch]);

  const hasActiveFilters =
    !!searchQuery ||
    status !== "all" ||
    !!dateRange?.startDate ||
    !!dateRange?.endDate ||
    (isSuperAdmin && !!merchantId);

  const isEmpty = !isLoading && !isError && content.length === 0;
  const isTrueEmpty = isEmpty && !hasActiveFilters;
  const isFilteredEmpty = isEmpty && hasActiveFilters;
  const filtersDisabled = isLoading || isError || isTrueEmpty;

  const renderContent = () => {
    if (isError) {
      return (
        <FetchFeedback
          message="Failed to load batch history. Please try again."
          onRetry={refetch}
          fullHeight={false}
          compact
        />
      );
    }

    if (isTrueEmpty) {
      return (
        <CustomEmptyState
          primary="No batch uploads yet"
          secondary={
            isSuperAdmin
              ? "No bulk disbursements have been submitted across any merchant."
              : "Upload a CSV file or manual entry to get started!"
          }
          height={250}
        />
      );
    }

    if (isFilteredEmpty) {
      return (
        <CustomEmptyState
          primary="No batches match your filters"
          secondary="Try adjusting your search or date range."
          height={250}
        />
      );
    }

    return (
      <BulkUploadsTable
        data={content}
        isLoading={isLoading}
        isFetching={isFetching}
        pagination={pagination}
        totalPages={totalPages}
        totalElements={totalElements}
        onPaginationChange={setPagination}
        onViewDetails={onViewDetails}
        onResume={onResume}
        isSuperAdmin={isSuperAdmin}
      />
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <BulkUploadsFilters
        filters={{ searchQuery, setSearchQuery, dateRange, setDateRange, status, setStatus }}
        merchantProps={{
          merchantId,
          setMerchantId,
          options: merchantOptions,
          isLoading: merchantOptionsLoading,
          isError: merchantOptionsError,
        }}
        exportProps={{ handleExport, isExporting }}
        disabled={filtersDisabled}
        isSuperAdmin={isSuperAdmin}
      />
      {renderContent()}
    </div>
  );
}

BulkUploadsContent.propTypes = {
  isSuperAdmin: PropTypes.bool,
};

export default BulkUploadsContent;
